import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import React from 'react';
import { useCommonFormUiTranslation } from '../hooks';
import { buildGeneralTab, buildHiddenTab, buildStyleTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { defaultWidgetLabelProvider, findSubFormProvider, getLabelAndValuePairsFromChildProps, handleWidgetUpdate, loadSubFormIfNeeded, makeNewConfigUnique, } from '../WidgetForm/Utils';
import { BaseLayoutColumn } from '../WidgetFormPanel/Components';
import { type } from './types';
const WidgetFormSubForm = (props) => {
    var _a;
    if (!props.internalData.loading) {
        return (React.createElement(BaseLayoutColumn, { spacing: 16, childrenEditorPath: 'children', childProps: (_a = props.childProps) !== null && _a !== void 0 ? _a : [], renderData: props.renderData }));
    }
    else {
        return (React.createElement(Box, { p: 4 },
            React.createElement(LinearProgress, null)));
    }
};
const WidgetFormSubFormEditor = (props) => {
    var _a, _b;
    const { t } = useCommonFormUiTranslation();
    let subFormProviderLabel;
    if ((_a = props.config.subFormProvider) === null || _a === void 0 ? void 0 : _a.type) {
        const subFormProvider = findSubFormProvider(props, props.config.subFormProvider.type);
        subFormProviderLabel = subFormProvider ? (subFormProvider.label) : (React.createElement("span", { style: { textDecoration: 'line-through' } }, (_b = props.config.subFormProvider) === null || _b === void 0 ? void 0 : _b.type));
    }
    else {
        subFormProviderLabel = React.createElement("em", null, t('common.none'));
    }
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => {
            var _a, _b;
            const subFormProviders = (_a = props.rootParams.subFormProviders) !== null && _a !== void 0 ? _a : [];
            const parentConfig = (_b = props.parentProps) === null || _b === void 0 ? void 0 : _b.config;
            const siblings = [];
            if (parentConfig) {
                const parentWidget = props.widgets.find((w) => w.type === parentConfig.type);
                if (parentWidget && parentWidget.childConfigs) {
                    parentWidget.childConfigs(parentConfig).forEach((siblingConfig) => {
                        if (siblingConfig !== props.config && siblingConfig.key) {
                            const component = '^' + siblingConfig.key;
                            switch (siblingConfig.type) {
                                case 'form.checkbox':
                                    siblings.push({
                                        value: component,
                                        label: t('widget.widgetCheckbox.checkbox') + ': ' + siblingConfig.label,
                                    });
                                    break;
                                case 'form.number':
                                    siblings.push({
                                        value: component,
                                        label: t('widget.widgetInput.number') + ': ' + siblingConfig.label,
                                    });
                                    break;
                                case 'form.text':
                                    siblings.push({
                                        value: component,
                                        label: t('widget.widgetInput.text') + ': ' + siblingConfig.label,
                                    });
                                    break;
                                case 'form.radio':
                                    siblings.push({
                                        value: component,
                                        label: t('widget.widgetRadio.radio') + ': ' + siblingConfig.label,
                                    });
                                    break;
                                case 'form.select':
                                    siblings.push({ value: component, label: t('common.select') + ': ' + siblingConfig.label });
                                    break;
                            }
                        }
                    });
                }
                siblings.push({ value: '$', label: 'Other' });
            }
            showWidgetEditDialog(props, endEditing, { title: 'Sub-Form' }, buildGeneralTab(props, {
                keyOptional: true,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onLoad: (data) => {
                    var _a;
                    if ((_a = data.subFormProvider) === null || _a === void 0 ? void 0 : _a.paramsFrom) {
                        data.subFormProvider.paramsFromFilter = true;
                        if (siblings.find((sibling) => sibling.value === data.subFormProvider.paramsFrom)) {
                            data.subFormProvider.paramsFromSibling = data.subFormProvider.paramsFrom;
                        }
                        else {
                            data.subFormProvider.paramsFromSibling = '$';
                            data.subFormProvider.paramsFromCustom = data.subFormProvider.paramsFrom;
                        }
                    }
                    return data;
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onSubmit: (data) => {
                    var _a;
                    if ((_a = data.subFormProvider) === null || _a === void 0 ? void 0 : _a.paramsFromFilter) {
                        if (data.subFormProvider.paramsFromSibling !== '$') {
                            data.subFormProvider.paramsFrom = data.subFormProvider.paramsFromSibling;
                        }
                        else {
                            data.subFormProvider.paramsFrom = data.subFormProvider.paramsFromCustom;
                        }
                        if (!data.subFormProvider.paramsFrom) {
                            delete data.subFormProvider.paramsFrom;
                        }
                    }
                    else {
                        delete data.subFormProvider.paramsFrom;
                    }
                    delete data.subFormProvider.paramsFromFilter;
                    delete data.subFormProvider.paramsFromSibling;
                    delete data.subFormProvider.paramsFromCustom;
                    return data;
                },
                children: subFormProviders.length > 0
                    ? [
                        {
                            type: 'form.select',
                            key: 'subFormProvider.type',
                            label: t('widget.widgetForm.subFormProvider'),
                            values: subFormProviders.map((subFormProvider) => ({
                                value: subFormProvider.type,
                                label: subFormProvider.label,
                            })),
                        },
                        {
                            type: 'form.panel',
                            children: [
                                {
                                    type: 'form.checkbox',
                                    key: 'subFormProvider.paramsFromFilter',
                                    label: t('widget.widgetForm.loadSubForm'),
                                },
                                {
                                    type: 'form.panel',
                                    hidden: {
                                        when: '^subFormProvider.paramsFromFilter',
                                        dataNotEq: true,
                                    },
                                    children: [
                                        {
                                            type: 'form.select',
                                            key: 'subFormProvider.paramsFromSibling',
                                            label: t('widget.widgetForm.component'),
                                            values: siblings,
                                        },
                                        {
                                            type: 'form.text',
                                            key: 'subFormProvider.paramsFromCustom',
                                            label: t('widget.widgetForm.componentQuery'),
                                            hidden: {
                                                when: '^subFormProvider.paramsFromSibling',
                                                dataNotEq: '$',
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                    ]
                    : [],
            }), buildHiddenTab(props, { t }), buildStyleTab(props));
        } }),
        React.createElement(Paper, { variant: 'outlined', sx: { p: 4 } },
            React.createElement(Typography, { variant: 'body2' },
                t('widget.widgetForm.subForm'),
                ": ",
                subFormProviderLabel))));
};
const defaultEmptyChildConfigs = [];
export const widgetFormSubForm = {
    type,
    init: (props) => {
        props.internalData.loadSubForm = true;
        loadSubFormIfNeeded(props);
    },
    update: (props) => {
        loadSubFormIfNeeded(props);
        return handleWidgetUpdate(props);
    },
    getLabelValueAsTextPairs: getLabelAndValuePairsFromChildProps,
    childConfigs: (config, props) => { var _a, _b, _c; return (_c = (_b = (_a = props === null || props === void 0 ? void 0 : props.internalData) === null || _a === void 0 ? void 0 : _a.subForm) === null || _b === void 0 ? void 0 : _b.children) !== null && _c !== void 0 ? _c : defaultEmptyChildConfigs; },
    component: WidgetFormSubForm,
    icon: React.createElement(BorderAllIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormSubFormEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, children: [] }),
};
